import { AircraftIconVariant, AircraftLayerFeature } from './types';
import { aircraftIconNameToDataMap } from './utils';
import { TAIL_NUM_VISIBLE_ZOOM_LEVEL } from './AircraftsLayer.styles';

/** Matches the atlas cell size, which is the icons' authored viewBox. */
export const ICON_SIZE_PX = 148;

/** Used when an aircraft's classification has no icon of its own. */
export const FALLBACK_ICON_NAME = 'tankerType2';

// How big to make the hit target's circle radius, relative to the icon's rendered
// size. 0.5 = circle in the icon's bbox.
const HIT_RADIUS_FRACTION = 0.6;
const HIT_RADIUS_MIN_PX = 16; // no smaller than this

export const getIconName = (feature: AircraftLayerFeature): string => {
  const { icon } = feature.properties;
  return aircraftIconNameToDataMap[icon] ? icon : FALLBACK_ICON_NAME;
};

export const getIconVariant = (
  feature: AircraftLayerFeature,
): AircraftIconVariant => {
  const isGround = feature.properties.altitude === 'ground';
  if (feature.properties.selected) {
    return isGround ? 'activeground' : 'active';
  }
  return isGround ? 'ground' : 'default';
};

export const isGroundedVariant = (variant: AircraftIconVariant): boolean =>
  variant === 'ground' || variant === 'activeground';

// Grounded helicopter rotors revolution period is slowed down by this factor.
export const GROUNDED_ROTOR_SLOWDOWN = 5;

export const getRotorRevolutionMs = (
  durationMs: number,
  isGrounded: boolean,
): number => (isGrounded ? durationMs * GROUNDED_ROTOR_SLOWDOWN : durationMs);

export const isAircraftLabelVisible = (
  feature: AircraftLayerFeature,
  zoom: number,
): boolean => {
  const { label, selected } = feature.properties;
  if (!label) {
    return false;
  }
  return selected || zoom >= TAIL_NUM_VISIBLE_ZOOM_LEVEL;
};

export const castsShadow = (feature: AircraftLayerFeature): boolean => {
  const { altitude } = feature.properties;
  return altitude != null && altitude !== 'ground';
};

export const getPosition = (
  feature: AircraftLayerFeature,
): [number, number] => [
  feature.geometry.coordinates[0],
  feature.geometry.coordinates[1],
];

export const getIconScaleMultiplier = (feature: AircraftLayerFeature): number =>
  aircraftIconNameToDataMap[getIconName(feature)]?.scale ?? 1;

export const getSize = (feature: AircraftLayerFeature): number =>
  ICON_SIZE_PX * getIconScaleMultiplier(feature);

export const getHitRadiusPx = (
  feature: AircraftLayerFeature,
  scale: number,
): number =>
  Math.max(HIT_RADIUS_MIN_PX, getSize(feature) * scale * HIT_RADIUS_FRACTION);

export const getRotorHubPixelOffset = (args: {
  hubX?: number;
  hubY: number;
  angleDeg: number;
  scale: number;
}): [number, number] => {
  const { hubX = ICON_SIZE_PX / 2, hubY, angleDeg, scale } = args;
  const offsetX = (hubX - ICON_SIZE_PX / 2) * scale;
  const offsetY = (hubY - ICON_SIZE_PX / 2) * scale;
  const angleRad = (angleDeg * Math.PI) / 180;
  const cos = Math.cos(angleRad);
  const sin = Math.sin(angleRad);
  return [offsetX * cos + offsetY * sin, offsetY * cos - offsetX * sin];
};

// 32-bit FNV-1a hash, normalized to [0, 1) interval. Any reasonable hashing algorithm will do,
// and this one looks good enough and is fast to compute.
const hashToUnitInterval = (value: string): number => {
  let hash = 0x811c9dc5;
  for (let index = 0; index < value.length; index += 1) {
    // eslint-disable-next-line no-bitwise -- hashing is the exception the rule means
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193);
  }
  // eslint-disable-next-line no-bitwise -- coerces the signed result back to unsigned
  return (hash >>> 0) / 2 ** 32;
};

// Returns a phase offset (in ms) based on its hex code (a static property of the aircraft's data).
export const getRotorPhaseOffsetMs = (
  hex: string,
  durationMs: number,
): number => hashToUnitInterval(hex) * durationMs;

export const getRotorSpinDeg = (args: {
  elapsedMs: number;
  durationMs: number;
  direction: number;
}): number => {
  const { elapsedMs, durationMs, direction } = args;
  if (!durationMs) {
    return 0;
  }
  return -1 * direction * 360 * ((elapsedMs % durationMs) / durationMs);
};

/**
 * Returns aircraft in altitude order and filters any without usable coordinates.
 */
export const sortFeaturesForDrawing = (
  features: AircraftLayerFeature[],
): AircraftLayerFeature[] => {
  const altitudeOf = (feature: AircraftLayerFeature): number =>
    typeof feature.properties.altitude === 'number'
      ? feature.properties.altitude
      : 0;

  return features
    .filter(
      (feature) =>
        Number.isFinite(feature.geometry.coordinates[0]) &&
        Number.isFinite(feature.geometry.coordinates[1]),
    )
    .sort((a, b) => altitudeOf(a) - altitudeOf(b));
};
